import React from 'react';

import Toast, { ToastProps } from './Toast';

export interface ToastItemData extends Omit<ToastProps, 'onClose'> {
  id: string | number;
}

export interface ToastContainerProps {
  toasts: ToastItemData[];
  removeToast: (id: string | number) => void;
}

/** Maximum number of toasts visible in the stack at once. */
const MAX_VISIBLE = 3;

/**
 * How many pixels each background card peeks above the front card.
 * Creates the visual depth / card-stack effect.
 */
const PEEK_OFFSET_PX = 8;

/**
 * Minimum toast height (matches Toast min-h-[84px]).
 * Used to compute the container height so peek cards don't overflow.
 */
const TOAST_BASE_HEIGHT_PX = 84;

/**
 * ToastContainer — renders up to MAX_VISIBLE toasts as a stacked card deck.
 *
 * Stack rules (Scenario 1):
 *  - Newest toast is the front card: full scale, full opacity, highest z-index.
 *  - Each older card peeks PEEK_OFFSET_PX above the front card, scaled down
 *    slightly and faded, giving a depth illusion.
 *  - Toasts beyond MAX_VISIBLE are hidden until a front card is dismissed.
 *
 * Manual dismiss (Scenario 2):   handled inside <Toast /> via the × button.
 * Auto-dismiss after 8s (Scenario 3): handled inside <Toast /> via duration prop.
 */
const ToastContainer = ({ toasts, removeToast }: ToastContainerProps) => {
  // Slice to the most recent MAX_VISIBLE entries; newest is last in the array.
  const visibleToasts = toasts.slice(-MAX_VISIBLE);

  // Container must be tall enough to show the front card plus every peek strip.
  const containerHeight =
    TOAST_BASE_HEIGHT_PX +
    Math.max(0, visibleToasts.length - 1) * PEEK_OFFSET_PX;

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 md:left-auto md:right-6 md:translate-x-0 z-[1200] pointer-events-none w-[500px]"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {/*
       * Relative wrapper sizes itself to the computed stack height so that
       * peek cards (absolutely positioned above the front card) are fully visible.
       */}
      <div className="relative" style={{ height: `${containerHeight}px` }}>
        {visibleToasts.map((toast, idx) => {
          /*
           * reverseIndex 0 = newest (front card, sits at bottom: 0).
           * reverseIndex n = oldest visible (peeks highest above the front card).
           *
           * Each card is offset upward by reverseIndex × PEEK_OFFSET_PX so its
           * top edge is visible above the card in front of it.
           */
          const reverseIndex = visibleToasts.length - 1 - idx;

          return (
            <div
              key={toast.id}
              className="absolute left-0 right-0 pointer-events-auto transition-all duration-300 ease-in-out"
              style={{
                // Front card anchored to container bottom; each older card steps up.
                bottom: `${reverseIndex * PEEK_OFFSET_PX}px`,
                // Older cards scale down slightly to reinforce depth.
                transform: `scale(${1 - reverseIndex * 0.03})`,
                // Scaling originates from the bottom-centre so cards shrink upward.
                transformOrigin: 'bottom center',
                // Subtle fade on background cards so the front card stands out.
                opacity: 1 - reverseIndex * 0.15,
                // Front card always on top; background cards layer behind it.
                zIndex: visibleToasts.length - reverseIndex,
              }}
            >
              <Toast
                id={toast.id}
                title={toast.title}
                message={toast.message}
                onClose={removeToast}
                duration={toast.duration}
                type={toast.type}
                isActive={reverseIndex === 0}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ToastContainer;
